/**
 * LeadDetailModal.tsx
 * Modal responsive con tabs para ver y editar campos de un lead
 */

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Save,
  User,
  DollarSign,
  Home,
  Clock,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Calendar,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react'
import api from '@/services/api'
import { toast } from 'react-toastify'
import { COUNTRY_CODE_OPTIONS } from '@/constants/countryCodes'
	
interface Lead {
  id: string
  name: string
  phone: string
  email: string
  budget_min: number
  budget_max: number
  budget_text: string
  budget_currency: string
  property_type: string
  zones: string[]
  purpose: string
  timeframe: string
  language: string
  country_code: string
  score: number
  temperature: 'hot' | 'warm' | 'cold'
  status: string
  notes: string
  next_contact_date: string
  last_contact_date: string
  interaction_count: number
  tags: string[]
  reclassified: boolean
  conversation_summary: string
  created_at: string
  archived: boolean
  conversation_id: string
}

interface Conversation {
  messages: Array<{ role: string; content: string; timestamp?: string }>
}

interface Activity {
  id: string
  activity_type: string
  title: string
  description: string
  scheduled_at: string
  status: string
  outcome: string | null
  created_at: string
}

interface LeadDetailModalProps {
  isOpen: boolean
  onClose: () => void
  lead: Lead
  onSave: () => void
}

const TABS = [
  { id: 'general', label: 'Datos Generales', labelShort: 'General', icon: User },
  { id: 'budget', label: 'Presupuesto', labelShort: 'Presup.', icon: DollarSign },
  { id: 'preferences', label: 'Preferencias', labelShort: 'Prefs', icon: Home },
  { id: 'tracking', label: 'Historial', labelShort: 'Historial', icon: Clock }
]

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nuevo' },
  { value: 'contacted', label: 'Contactado' },
  { value: 'qualified', label: 'Calificado' },
  { value: 'negotiating', label: 'Negociando' },
  { value: 'closed_won', label: 'Ganado' },
  { value: 'closed_lost', label: 'Perdido' }
]

const TEMPERATURE_OPTIONS = [
  { value: 'hot', label: '🔥 Caliente', color: 'text-red-400' },
  { value: 'warm', label: '☀️ Tibio', color: 'text-yellow-400' },
  { value: 'cold', label: '❄️ Frío', color: 'text-blue-400' }
]

const ACTIVITY_ICONS: Record<string, string> = {
  call: '📞',
  whatsapp: '💬',
  email: '📧',
  visit: '🏠',
  meeting: '🤝',
  property_show: '🔑',
  follow_up: '📋',
  note: '📝'
}

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({ isOpen, onClose, lead, onSave }) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('general')
  const [formData, setFormData] = useState<any>({})
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [showConversation, setShowConversation] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [activities, setActivities] = useState<Activity[]>([])
  const [loadingActivities, setLoadingActivities] = useState(false)

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        phone: lead.phone || '',
        email: lead.email || '',
        budget_min: lead.budget_min || '',
        budget_max: lead.budget_max || '',
        budget_text: lead.budget_text || '',
        budget_currency: lead.budget_currency || 'MXN',
        property_type: lead.property_type || '',
        zones: lead.zones || [],
        purpose: lead.purpose || '',
        timeframe: lead.timeframe || '',
        language: lead.language || 'es',
        country_code: lead.country_code || '+52',
        score: lead.score || 0,
        temperature: lead.temperature || 'warm',
        status: lead.status || 'new',
        notes: lead.notes || '',
        next_contact_date: lead.next_contact_date || '',
        tags: lead.tags || []
      })

      if (lead.conversation_id) {
        fetchConversation(lead.conversation_id)
      }
      fetchActivities(lead.id)
    }
  }, [lead])

  const fetchConversation = async (conversationId: string) => {
    try {
      const response = await api.get(`/api/agents/conversations/${conversationId}`)
      setConversation(response.data.conversation)
    } catch (error) {
      console.error('Error fetching conversation:', error)
    }
  }

  const fetchActivities = async (leadId: string) => {
    setLoadingActivities(true)
    try {
      const response = await api.get(`/api/agents/activities?lead_id=${leadId}`)
      setActivities(response.data.activities || [])
    } catch (error) {
      console.error('Error fetching activities:', error)
    } finally {
      setLoadingActivities(false)
    }
  }

  const handleSave = async () => {
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.warning('Formato de email inválido')
      return
    }

    if (formData.phone) {
      const digitsOnly = formData.phone.replace(/\D/g, '')
      if (digitsOnly.length < 10) {
        toast.warning('Teléfono debe tener mínimo 10 dígitos')
        return
      }
    }

    if (formData.budget_min && formData.budget_max && parseFloat(formData.budget_min) > parseFloat(formData.budget_max)) {
      toast.warning('Presupuesto mínimo no puede ser mayor al máximo')
      return
    }

    setSaving(true)
    try {
      await api.put(`/api/agents/leads/${lead.id}`, formData)
      toast.success('Lead actualizado exitosamente')
      onSave()
      onClose()
    } catch (error: any) {
      console.error('Error saving lead:', error)
      toast.error(error.response?.data?.error || 'Error al guardar cambios')
    } finally {
      setSaving(false)
    }
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] })
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData({ ...formData, tags: formData.tags.filter((t: string) => t !== tagToRemove) })
  }

  const handleAddZone = (zone: string) => {
    if (zone.trim() && !formData.zones.includes(zone.trim())) {
      setFormData({ ...formData, zones: [...formData.zones, zone.trim()] })
    }
  }

  const handleRemoveZone = (zoneToRemove: string) => {
    setFormData({ ...formData, zones: formData.zones.filter((z: string) => z !== zoneToRemove) })
  }

  const copyConversation = () => {
    if (!conversation) return
    const text = conversation?.messages?.length > 0
      ? conversation.messages.map(m => `[${m.role === 'user' ? 'Usuario' : 'Bot'}]: ${m.content}`).join('\n\n')
      : ''
    if (!text) return
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleGoToActivities = () => {
    onClose()
    navigate(`/dashboard/leads?tab=seguimiento&lead_id=${lead.id}`)
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Sin fecha'
    return new Date(dateString).toLocaleDateString('es-MX', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getActivityStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400'
      case 'pending': return 'text-yellow-400'
      case 'overdue': return 'text-red-400'
      case 'cancelled': return 'text-gray-400'
      default: return 'text-gray-400'
    }
  }

  const getActivityStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return 'Completada'
      case 'pending': return 'Pendiente'
      case 'overdue': return 'Vencida'
      case 'cancelled': return 'Cancelada'
      default: return status
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 rounded-xl w-full max-w-6xl h-[95vh] sm:h-[90vh] flex flex-col overflow-hidden shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="flex items-start sm:items-center justify-between p-4 sm:p-6 border-b border-white/10 gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-2xl font-bold text-white truncate">
                {lead.name || 'Lead sin nombre'}
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm mt-0.5 sm:mt-1 truncate">
                {lead.phone} • Creado: {new Date(lead.created_at).toLocaleDateString('es-MX')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
            </button>
          </div>

          {/* Tabs - Scroll horizontal en móvil */}
          <div className="overflow-x-auto border-b border-white/10 flex-shrink-0">
            <div className="flex min-w-max px-2 sm:px-6 pt-2 sm:pt-4">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-t-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-slate-800 text-white border-t-2 border-blue-400'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="text-xs sm:text-sm font-medium">
                    <span className="sm:hidden">{tab.labelShort}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {/* TAB: Datos Generales */}
            {activeTab === 'general' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Nombre</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Teléfono</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">País</label>
                    <select
                      value={formData.country_code}
                      onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      {COUNTRY_CODE_OPTIONS.map(c => (
                        <option key={c.value} value={c.value}>{c.label} {c.country}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Temperatura</label>
                    <select
                      value={formData.temperature}
                      onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      {TEMPERATURE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">
                    Score (0-100)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <div className="text-center text-white font-bold text-xl sm:text-2xl mt-1 sm:mt-2">
                    {formData.score}/100
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.tags?.length > 0 && formData.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="bg-blue-500/20 text-blue-400 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm flex items-center gap-1.5"
                      >
                        <span>{tag}</span>
                        <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                      placeholder="Agregar tag..."
                      className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleAddTag}
                      className="px-3 sm:px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Notas</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Notas sobre este lead..."
                  />
                </div>
              </div>
            )}

            {/* TAB: Presupuesto */}
            {activeTab === 'budget' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Presupuesto Mín</label>
                    <input
                      type="number"
                      value={formData.budget_min}
                      onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="500000"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Presupuesto Máx</label>
                    <input
                      type="number"
                      value={formData.budget_max}
                      onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      placeholder="2000000"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Moneda</label>
                    <select
                      value={formData.budget_currency}
                      onChange={(e) => setFormData({ ...formData, budget_currency: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="MXN">MXN (Pesos)</option>
                      <option value="USD">USD (Dólares)</option>
                      <option value="EUR">EUR (Euros)</option>
                      <option value="ARS">ARS (Argentina)</option>
                      <option value="COP">COP (Colombia)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Timeframe</label>
                    <select
                      value={formData.timeframe}
                      onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="urgente">Urgente (1 mes)</option>
                      <option value="corto">Corto (1-3 meses)</option>
                      <option value="medio">Mediano (3-6 meses)</option>
                      <option value="largo">Largo (6+ meses)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Presupuesto (Texto)</label>
                  <textarea
                    value={formData.budget_text}
                    onChange={(e) => setFormData({ ...formData, budget_text: e.target.value })}
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder='Ej: "Flexible según la zona"'
                  />
                </div>
              </div>
            )}

            {/* TAB: Preferencias */}
            {activeTab === 'preferences' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Tipo Propiedad</label>
                    <select
                      value={formData.property_type}
                      onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="casa">Casa</option>
                      <option value="departamento">Departamento</option>
                      <option value="terreno">Terreno</option>
                      <option value="comercial">Comercial</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Propósito</label>
                    <select
                      value={formData.purpose}
                      onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="compra">Compra</option>
                      <option value="renta">Renta</option>
                      <option value="inversion">Inversión</option>
                      <option value="leasing">Leasing</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Idioma</label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm sm:text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                      <option value="pt">Português</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Zonas de Interés</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.zones?.length > 0 && formData.zones.map((zone: string) => (
                      <span
                        key={zone}
                        className="bg-green-500/20 text-green-400 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm flex items-center gap-1.5"
                      >
                        <span>{zone}</span>
                        <button onClick={() => handleRemoveZone(zone)} className="hover:text-red-400">×</button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddZone((e.target as HTMLInputElement).value)
                        ;(e.target as HTMLInputElement).value = ''
                      }
                    }}
                    placeholder="Escribe zona + Enter..."
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Resumen IA</label>
                  <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-gray-300 text-sm">
                    {lead.conversation_summary || 'Sin resumen disponible'}
                  </div>
                </div>
              </div>
            )}

            {/* TAB: Historial */}
            {activeTab === 'tracking' && (
              <div className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Creado</label>
                    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-gray-300 text-sm">
                      {new Date(lead.created_at).toLocaleString('es-MX')}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Último Contacto</label>
                    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-gray-300 text-sm">
                      {lead.last_contact_date ? new Date(lead.last_contact_date).toLocaleString('es-MX') : 'Nunca'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Próximo Contacto</label>
                    <input
                      type="date"
                      value={formData.next_contact_date}
                      onChange={(e) => setFormData({ ...formData, next_contact_date: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5 sm:mb-2">Interacciones</label>
                    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2 text-gray-300 text-sm">
                      {lead.interaction_count || 0} interacciones
                    </div>
                  </div>
                </div>

                {lead.reclassified && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 sm:p-4">
                    <p className="text-purple-400 text-xs sm:text-sm font-medium">🔄 Lead re-clasificado por IA</p>
                  </div>
                )}

                {lead.archived && (
                  <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-400 text-xs sm:text-sm font-medium">📦 Lead archivado</p>
                  </div>
                )}

                {/* Actividades */}
                <div className="border-t border-white/10 pt-4 sm:pt-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                    <h3 className="text-sm sm:text-lg font-semibold text-white flex items-center gap-2">
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                      <span className="hidden sm:inline">Actividades</span>
                      <span className="sm:hidden">Actividades</span>
                    </h3>
                    <button
                      onClick={handleGoToActivities}
                      className="flex items-center gap-1.5 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors text-xs sm:text-sm font-medium"
                    >
                      <span className="hidden sm:inline">Ver todas</span>
                      <span className="sm:hidden">Ver</span>
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </div>

                  {loadingActivities ? (
                    <div className="flex items-center justify-center py-6 sm:py-8">
                      <Loader className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-400" />
                    </div>
                  ) : activities.length === 0 ? (
                    <div className="text-center py-6 sm:py-8 bg-slate-800/50 rounded-lg">
                      <Calendar className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-600 mb-2 sm:mb-3" />
                      <p className="text-gray-400 text-sm mb-2 sm:mb-3">Sin actividades</p>
                      <button onClick={handleGoToActivities} className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium">
                        + Crear actividad
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 sm:max-h-64 overflow-y-auto">
                      {activities.slice(0, 5).map((activity) => (
                        <div key={activity.id} className="flex items-center justify-between p-2.5 sm:p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            <span className="text-lg sm:text-xl flex-shrink-0">{ACTIVITY_ICONS[activity.activity_type] || '📌'}</span>
                            <div className="min-w-0 flex-1">
                              <p className="text-white font-medium text-xs sm:text-sm truncate">{activity.title}</p>
                              <p className="text-gray-400 text-xs truncate">{formatDateTime(activity.scheduled_at)}</p>
                            </div>
                          </div>
                          <span className={`text-xs font-medium flex-shrink-0 ${getActivityStatusColor(activity.status)}`}>
                            {activity.status === 'completed' && <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                            {activity.status === 'overdue' && <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                          </span>
                        </div>
                      ))}
                      {activities.length > 5 && (
                        <p className="text-center text-gray-400 text-xs py-2">+{activities.length - 5} más</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Conversación Original */}
            {conversation && (
              <div className="mt-4 sm:mt-6 border-t border-white/10 pt-4 sm:pt-6">
                <button
                  onClick={() => setShowConversation(!showConversation)}
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  <span className="text-white font-medium text-sm sm:text-base">💬 Conversación Original</span>
                  {showConversation ? <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                </button>

                {showConversation && (
                  <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3 max-h-64 sm:max-h-96 overflow-y-auto">
                    <button
                      onClick={copyConversation}
                      className="mb-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center gap-2 text-xs sm:text-sm"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                      <span>{copied ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                    {conversation?.messages?.length > 0 && conversation.messages.map((msg: any, idx: number) => (
                      <div key={idx} className={`p-2.5 sm:p-3 rounded-lg ${msg.role === 'user' ? 'bg-blue-500/20 ml-4 sm:ml-8' : 'bg-white/5 mr-4 sm:mr-8'}`}>
                        <p className="text-xs text-gray-400 mb-1">
                          {msg.role === 'user' ? '👤 Usuario' : '🤖 Bot'}
                          {msg.timestamp && ` • ${new Date(msg.timestamp).toLocaleTimeString('es-MX')}`}
                        </p>
                        <p className="text-white text-xs sm:text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-white/10">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2.5 sm:py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors text-sm sm:text-base"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 sm:px-6 py-2.5 sm:py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Guardando...' : 'Guardar Cambios'}</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default LeadDetailModal
